/*
 * pz-cart-fees.js
 * デザインシミュレーターのカート全体の料金の行（製版代・洗浄料・サイズアップ）をそろえる（段階 2a）。
 * 設計: printeez-portal/docs/design-simulator/same-print-product-switch.html 4.6・8.3
 *
 * - カート（/cart.js）の行のうち、デザインの行（商品のタイプが PZ Design Product。昔の行は隠し項目 _pz_design_version）と、
 *   隠し項目（_pz_ で始まる）を持つ行（料金の行など）だけを portal（App Proxy の design-cart-fees）に送る。
 *   送るのは key・variant_id・quantity と、隠し項目・「内容」だけ（秘密鍵 _pz_design_key は送らない）。
 *   2026-10-29: 新しいデザインの行には隠し項目が無い。portal が行のバリアントから版・カラー・サイズを引く
 *   版ID・前に作った版か・金額は portal が決める（ここでは数えない）
 * - 返ってきた「足す行」を /cart/add.js で足してから、「消す行」を /cart/change.js で消す
 *   （途中で失敗しても料金の行が足りなくならない側。足せなければ消さない）。足す・消すのに失敗したら（429 など）
 *   そろえたことにせず、次の問い合わせでやり直す（続けて失敗したら同じカートでは問い合わせず、知らせを出したままにする）
 * - 計算している間は「ご購入手続きへ」を押せなくする（html に pz-cart-fees-busy、ボタンに disabled、横に「計算しています」）。
 *   カートのページのデザインの行が待ち表示（応答が届くまで行を出していない）の間も同じ
 * - アクリルキーホルダーの個数が 1 カラーの最少個数より少ないまとまりがある間も、同じ止め方で押せなくする（html に
 *   pz-cart-fees-blocked、横に理由）。見るのは、今のカートに合う受け口の応答の items[].acrylic.minQty とその版 × カラーの個数
 *   （カートのページだけ。応答が無い・今のカートと合わない・前の portal なら止めない）。個数を直す・削除すると、次の応答で戻る
 * - 値段がまだ入っていないデザインの行（/cart.js の値段が 0 か商品名が無い。作ったばかりのバリアントを足した直後、お店が数秒〜数分
 *   こう返すことがあり、カートの合計も 0 になる）がある間も、計算中と同じ止め方で押せなくする（カートのページだけ）。見るのは、
 *   ここでカートを読むたび（開いたとき・料金の行を当てた後・枚数を変えた後。そのための読み直しは増やさない）。見つけたら
 *   priceWaitIntervalMs ごとに /cart.js を読み直し、値段が入ったら描き直して（PzCartRefreshSections）戻す。priceWaitMaxMs たっても
 *   入らなければ、止めたまま知らせを出す。料金の行・ふつうの商品（0 円が正しい商品）は見ない
 * - 前に数えたときと同じカート（隠し項目のある行の key と数量が同じ）なら問い合わせない
 * - 枚数を変える（changeQuantity。段階 2b のカートのページのサイズごとの －・＋）: change を付けて問い合わせ、portal が
 *   よいと言えば（増やすときは在庫を確かめる）/cart/change.js で枚数を変えてから、料金の行を足す・消す。だめなら変えずに知らせる
 * - アクリルキーホルダーの区分の付け替え（応答の retier。個数が変わって料金の区分が変わると、portal が新しい区分の行を「足す行」の
 *   先頭に、前の行を「消す行」に入れて返す）: 返ってきた行をそのまま足す（秘密鍵は行に写さない = 注文のデータに鍵を残さない。
 *   2026-10-29 まで写していた）。枚数を変える行ごと置き換わるときは、その行の枚数は変えずに足して消す（足せなければカートを変えない）
 * - 行を消す（removeLines）: /cart/update.js で消してから、料金の行をそろえる
 * - 問い合わせ・枚数の変更・行を消すのは 1 つずつ順に行う（料金の行を同時に書き換えない）
 * - 受け口の最後の応答（カートの表示の値 = 面の画像・版ID・単価の内訳）を覚え、document に pz:cart-fees を出す。
 *   専用ページから /cart へ移ったときにすぐ出せるよう、sessionStorage にも残す（lastResponse / responseFor）
 *
 * 使う場所: 専用ページ（カートに入れた後、/cart へ移る前。assets/pz-design-embed.js）と、
 * カートのページ（snippets/pz-cart-options.liquid。カートが変わるたび。表示は assets/pz-design-cart.js）。
 */
(function () {
  'use strict';
  if (window.PzCartFees) return;

  var CONFIG = {
    /* App Proxy（printeez.jp/apps/printeez/* → portal /api/proxy/*） */
    endpoint: '/apps/printeez/design-cart-fees',
    cartJs: '/cart.js',
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cartUpdate: '/cart/update.js',
    busyClass: 'pz-cart-fees-busy',
    /* 最少個数より少ないまとまりがあって止めている間（html に付ける。止め方は busyClass と同じ） */
    blockedClass: 'pz-cart-fees-blocked',
    checkoutSelector: '#checkout, button[name="checkout"], input[name="checkout"]',
    /* 失敗したとき 1 回だけやり直すまでの時間 */
    retryDelayMs: 1500,
    /* 料金の行を足す・消すのに続けて失敗したとき、問い合わせてやり直す回数（これを超えたら同じカートではやり直さない） */
    applyRetries: 2,
    /* 計算中のまま止まったときに、ボタンを戻すまでの時間 */
    busyTimeoutMs: 20000,
    /* 値段がまだ入っていないデザインの行があるとき、/cart.js を読み直す間隔と、あきらめるまでの時間 */
    priceWaitIntervalMs: 1500,
    priceWaitMaxMs: 40000,
    /* 最後の応答を sessionStorage に残す鍵と、使ってよい古さ */
    storageKey: 'pz-cart-fees-last',
    storageMaxAgeMs: 30 * 60 * 1000
  };
  /* 昔の行の隠し項目（新しい行には無い）。デザインの行は商品のタイプで見分ける（cart.js の product_type） */
  var DESIGN_VERSION = '_pz_design_version';
  var DESIGN_PRODUCT_TYPE = 'PZ Design Product';
  var FEE_SET = '_pz_fee_set';
  var EDIT_KEY = '_pz_design_key';
  /* 数える単位（昔のアクリルキーホルダーの行の「個」。新しい行は商品のオプション名「料金区分」で見分ける） */
  var UNIT = '_pz_unit';
  var PIECES_OPTION = '料金区分';
  var SECRET_KEYS = [EDIT_KEY];
  var MESSAGES = {
    changeFailed: '枚数を変えられませんでした。時間をおいてもう一度お試しください。',
    /* 数える単位が「個」の行（アクリルキーホルダー。商品のオプション名「料金区分」。昔の行は項目 _pz_unit） */
    changeFailedPieces: '個数を変えられませんでした。時間をおいてもう一度お試しください。',
    cartChanged: 'カートの内容が変わりました。ページを読み込み直してください。',
    removeFailed: '削除できませんでした。時間をおいてもう一度お試しください。',
    /* デザインの行の値段が、待っても入らなかった（「ご購入手続きへ」は止めたまま） */
    priceUnavailable: '料金を読み込めませんでした。ページを再読み込みしてください。',
    /* アクリルキーホルダーの 1 カラーの個数が最少個数（n 個）より少ない（そのまとまりの下と「ご購入手続きへ」の横に出す） */
    minQuantity: function (n) {
      return 'アクリルキーホルダーは1カラー' + n + '個からご注文いただけます。' + n + '個以上にするか、削除してください';
    }
  };

  var state = {
    /* 最後にそろえたカート（隠し項目のある行の key と数量） */
    synced: null,
    /* 順に行う処理（問い合わせ・枚数の変更・行を消す）のつながりと、その数 */
    queue: Promise.resolve(),
    active: 0,
    /* まだ始まっていない問い合わせ（続けて頼まれたら 1 回にまとめる） */
    pendingSync: null,
    /* portal に問い合わせて料金の行を作り直している間 */
    calling: false,
    /* カートが変わった印（計算が始まる前からボタンを止める）と、その回数 */
    dirty: false,
    dirtySeq: 0,
    busyTimer: null,
    /* 最後の結果の知らせ（null = なし） */
    message: null,
    /* 受け口の最後の応答と、そのときのカート（signature） */
    last: null,
    /* 最少個数より少ないまとまりを調べた応答（last）と、その結果の文（無ければ空）。同じ応答を何度も調べない */
    blockFor: null,
    blockText: '',
    /*
     * 値段がまだ入っていないデザインの行を待っている間（null = 待っていない）:
     * { since: 待ち始めた時刻, failed: 待っても入らなかった, timer: 次の読み直し, ending: 入ったので描き直している }
     */
    priceWait: null,
    /* 最後に読んだカートの数（/cart.js の item_count。読んでいなければ null） */
    itemCount: null,
    /* 料金の行を足す・消すのに続けて失敗した回数と、やり直すのをやめたカート（signature。null = やめていない） */
    applyFailures: 0,
    failedSig: null
  };

  function log() {
    if (!window.console || !console.log) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[pz-cart-fees]');
    console.log.apply(console, args);
  }

  function warn() {
    if (!window.console || !console.warn) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[pz-cart-fees]');
    console.warn.apply(console, args);
  }

  function props(item) {
    return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  /* デザインの行か（版の商品 = 商品のタイプが PZ Design Product。昔の行は隠し項目 _pz_design_version もある） */
  function isDesignLine(item) {
    return !!item && (item.product_type === DESIGN_PRODUCT_TYPE || !!props(item)[DESIGN_VERSION]);
  }

  /* 数える単位が「個」の行か（アクリルキーホルダー = 2 つ目のオプションが「料金区分」。昔の行は項目 _pz_unit も） */
  function isPiecesLine(item) {
    if (props(item)[UNIT] === '個') return true;
    var o = item && item.options_with_values;
    return Array.isArray(o) && o.length > 1 && !!o[1] && o[1].name === PIECES_OPTION;
  }

  /* デザインの行か、隠し項目（_pz_ で始まる）を持つ行（料金の行・デザインのオプションの行） */
  function isPzLine(item) {
    if (isDesignLine(item)) return true;
    var p = props(item);
    for (var k in p) {
      if (Object.prototype.hasOwnProperty.call(p, k) && k.indexOf('_pz_') === 0) return true;
    }
    return false;
  }

  /* portal に数えてもらう必要がある行があるか（デザインの行か、カート全体の料金の行） */
  function needsFees(items) {
    for (var i = 0; i < items.length; i++) {
      if (isDesignLine(items[i]) || props(items[i])[FEE_SET]) return true;
    }
    return false;
  }

  function signature(items) {
    return items
      .filter(isPzLine)
      .map(function (it) { return String(it.key) + ':' + String(it.quantity); })
      .sort()
      .join('|');
  }

  function payloadLines(items) {
    return items.filter(isPzLine).map(function (it) {
      var p = props(it);
      var out = {};
      Object.keys(p).forEach(function (k) {
        if (SECRET_KEYS.indexOf(k) >= 0) return;
        if (k.charAt(0) === '_' || k === '内容') out[k] = String(p[k]);
      });
      return { key: String(it.key), variant_id: Number(it.variant_id), quantity: Number(it.quantity), properties: out };
    });
  }

  function readJson(res) {
    return res.text().then(function (text) {
      var json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch (e) {
        json = null;
      }
      return {
        ok: res.ok,
        status: res.status,
        json: json && typeof json === 'object' ? json : null,
        retryAfter: res.headers ? Number(res.headers.get('Retry-After')) || 0 : 0
      };
    });
  }

  function readCart() {
    return fetch(CONFIG.cartJs, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  /* カートを読む。読むたびに、値段がまだ入っていないデザインの行が無いかも見る（watchPrices） */
  function getCart() {
    return readCart().then(function (cart) {
      if (cart && typeof cart.item_count === 'number') state.itemCount = cart.item_count;
      watchPrices(cart);
      return cart;
    });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(readJson);
  }

  function wait(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  /* 受け口に問い合わせる（5xx・通信の失敗は 1 回だけやり直す。429 は待ってからやり直す） */
  function askPortal(body) {
    var attempt = function (n) {
      return postJson(CONFIG.endpoint, body).then(
        function (r) {
          if (r.json && r.json.ok === true) return r;
          var retry = n === 0 && (r.status >= 500 || r.status === 429);
          if (!retry) return r;
          var delay = r.status === 429 ? Math.min(5, Math.max(1, r.retryAfter)) * 1000 : CONFIG.retryDelayMs;
          return wait(delay).then(function () { return attempt(1); });
        },
        function (e) {
          if (n > 0) throw e;
          return wait(CONFIG.retryDelayMs).then(function () { return attempt(1); });
        }
      );
    };
    return attempt(0);
  }

  /* 応答の区分の付け替え（アクリルキーホルダー。retier）のうち、その行（key）を置き換えるもの（無ければ null） */
  function retierOf(res, key) {
    var list = res && Array.isArray(res.retier) ? res.retier : [];
    for (var i = 0; i < list.length; i++) {
      var r = list[i];
      if (r && Array.isArray(r.fromKeys) && r.fromKeys.map(String).indexOf(String(key)) >= 0) return r;
    }
    return null;
  }

  /* 付け替えで足した行の key（その区分のバリアントのデザインの行のうち、置き換えた行でないもの。見つからなければ null） */
  function retierLineKey(r, items) {
    var from = r.fromKeys.map(String);
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (Number(it.variant_id) !== Number(r.variantId) || !isDesignLine(it)) continue;
      if (from.indexOf(String(it.key)) < 0) return String(it.key);
    }
    return null;
  }

  /*
   * 足す行（/cart/add.js の items）。返ってきた行をそのまま足す。区分の付け替え（応答の retier）の行にも、置き換える行の秘密鍵
   * _pz_design_key は写さない（2026-10-29: 注文のデータに鍵を残さない。以前は写していた）。「デザインを編集」の鍵はエディタのカートが
   * 別に持つ（assets/pz-editor-cart.js の editKeyFor）。応答そのものは書き換えない（覚える応答 = sessionStorage に鍵を残さない）
   */
  function linesToAdd(res) {
    return Array.isArray(res.add) ? res.add : [];
  }

  /*
   * 足す行を足してから、消す行を消す。足せなければ消さない。items は今のカートの行（以前は付け替えの行に秘密鍵を写すのに使った。今は使わない）。
   * 戻り値: { changed: カートを変えたか, failed: 足せない・消せない行があったか（429・通信の失敗など。そろえたことにしない） }
   */
  function applyChanges(res, items) {
    var add = linesToAdd(res);
    var remove = Array.isArray(res.remove) ? res.remove : [];
    var out = { changed: false, failed: false };
    var chain = Promise.resolve(true);
    if (add.length > 0) {
      chain = postJson(CONFIG.cartAdd, { items: add }).then(
        function (r) {
          if (r.ok) {
            out.changed = true;
            return true;
          }
          var d = r.json || {};
          warn('料金の行を足せません', r.status, d.description || '');
          out.failed = true;
          return false;
        },
        function (e) {
          warn('料金の行を足せません', e && e.message);
          out.failed = true;
          return false;
        }
      );
    }
    return chain.then(function (added) {
      if (!added) return out;
      return remove
        .reduce(function (p, key) {
          return p.then(function () {
            return postJson(CONFIG.cartChange, { id: key, quantity: 0 }).then(
              function (r) {
                if (r.ok) {
                  out.changed = true;
                  return;
                }
                warn('料金の行を消せません', r.status);
                out.failed = true;
              },
              function (e) {
                warn('料金の行を消せません', e && e.message);
                out.failed = true;
              }
            );
          });
        }, Promise.resolve())
        .then(function () {
          return out;
        });
    });
  }

  /*
   * 料金の行を当てた結果を覚える（sig = 当てた後のカート）。失敗した行があれば、そろえたことにしない（state.synced = null で、
   * 次の問い合わせは前と同じカートでも行う）。続けて applyRetries 回を超えて失敗したら、このカートではもうやり直さない（failedSig）
   */
  function noteApplied(sig, failed) {
    if (!failed) {
      state.synced = sig;
      state.applyFailures = 0;
      state.failedSig = null;
      return;
    }
    state.synced = null;
    state.applyFailures++;
    state.failedSig = state.applyFailures > CONFIG.applyRetries ? sig : null;
  }

  /* ------------------------------------------------------------------ */
  /* 「ご購入手続きへ」を止める・知らせる                                  */
  /* ------------------------------------------------------------------ */

  function injectStyle() {
    if (document.getElementById('PzCartFeesStyle')) return;
    var style = document.createElement('style');
    style.id = 'PzCartFeesStyle';
    style.textContent =
      '.pz-cart-fees-note{font-size:12px;color:#767676;margin:8px 0 0;text-align:center;line-height:1.5}' +
      '.pz-cart-fees-note--error{color:#c0392b}' +
      '.pz-cart-fees-busy .cart__dynamic-checkout-buttons,.pz-cart-fees-busy .additional-checkout-buttons,' +
      '.pz-cart-fees-blocked .cart__dynamic-checkout-buttons,.pz-cart-fees-blocked .additional-checkout-buttons{pointer-events:none;opacity:.5}';
    (document.head || document.documentElement).appendChild(style);
  }

  /*
   * 計算中か: カートが変わってまだ数え直していない（dirty）か、portal に問い合わせて料金の行を作り直している（calling）。
   * カートの確かめ（/cart.js）だけの間は止めない（デザインの行が無いカートで「ご購入手続きへ」がちらつかないように）
   */
  function isBusy() {
    return !!(state.calling || state.dirty || (state.priceWait && !state.priceWait.failed) || designLinesWaiting());
  }

  /*
   * デザインの行が待ち表示のまま（受け口の応答が届くまで行を出していない。assets/pz-design-cart.js。8 秒で出す）か。
   * 何を払うのか見えない間は「ご購入手続きへ」も押せなくする
   */
  function designLinesWaiting() {
    return !!document.querySelector('[data-pz-dc][data-pz-dc-state="wait"]');
  }

  /* ------------------------------------------------------------------ */
  /* 値段がまだ入っていないデザインの行                                   */
  /* ------------------------------------------------------------------ */

  /*
   * デザインの行（版の商品。昔の行は _pz_design_version がある）で、値段がまだ入っていないもの: 商品名が無いか、値段が 0（値引きで 0 になった行は
   * 元の値段があるので当たらない）。料金の行・ふつうの商品は見ない
   */
  function isUnpriced(item) {
    if (!isDesignLine(item)) return false;
    if (item.product_title == null || item.product_title === '') return true;
    return !(Number(item.price) > 0) && !(Number(item.original_price) > 0);
  }

  function hasUnpriced(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) if (isUnpriced(items[i])) return true;
    return false;
  }

  /*
   * カートを読んだたびに呼ぶ（getCart。そのための読み直しはしない）。値段がまだ入っていないデザインの行があれば、入るまで
   * 「ご購入手続きへ」を止めて読み直す。止めるのはカートのページ（#PzCartFeeState がある）だけ
   */
  function watchPrices(cart) {
    if (!hasUnpriced(cart)) {
      if (state.priceWait) endPriceWait();
      return;
    }
    if (state.priceWait || !document.getElementById('PzCartFeeState')) return;
    warn('値段がまだ入っていないデザインの行があります。入るまで読み直します');
    state.priceWait = { since: Date.now(), failed: false, timer: null, ending: false };
    schedulePriceCheck(state.priceWait);
    applyCheckoutState();
  }

  /* priceWaitIntervalMs 後に /cart.js を読み直す。まだなら繰り返し、priceWaitMaxMs たったら止めたまま知らせる */
  function schedulePriceCheck(w) {
    var again = function () {
      if (state.priceWait !== w) return;
      if (Date.now() - w.since < CONFIG.priceWaitMaxMs) return schedulePriceCheck(w);
      warn('デザインの行の値段が入りません。「ご購入手続きへ」は止めたままにします');
      w.failed = true;
      applyCheckoutState();
    };
    w.timer = window.setTimeout(function () {
      w.timer = null;
      if (state.priceWait !== w) return;
      readCart().then(function (cart) {
        if (state.priceWait !== w) return;
        if (hasUnpriced(cart)) again();
        else endPriceWait();
      }, again);
    }, CONFIG.priceWaitIntervalMs);
  }

  /* 値段が入った（値段の無い行が無くなった）: 行と右の合計を描き直してから、止めを外す */
  function endPriceWait() {
    var w = state.priceWait;
    if (!w || w.ending) return;
    w.ending = true;
    if (w.timer) window.clearTimeout(w.timer);
    w.timer = null;
    var done = function () {
      if (state.priceWait === w) state.priceWait = null;
      applyCheckoutState();
    };
    Promise.resolve()
      .then(function () {
        return typeof window.PzCartRefreshSections === 'function' ? window.PzCartRefreshSections() : null;
      })
      .then(done, done);
  }

  /*
   * アクリルキーホルダーのまとまり（受け口の表示の値 items[] の 1 つ = 版 × カラー）の個数が、1 カラーの最少個数より少ないとき、
   * その最少個数（そうでなければ 0。最少個数が分からない = 料金表を読めなかった・前の portal も 0）
   */
  function shortOfMinimum(view) {
    var min = view && view.acrylic ? Number(view.acrylic.minQty) || 0 : 0;
    var qty = view ? Number(view.quantity) || 0 : 0;
    return min > 0 && qty > 0 && qty < min ? min : 0;
  }

  /*
   * 最少個数より少ないまとまりがあって「ご購入手続きへ」を止める理由の文（止めなければ空）。カートのページ（#PzCartFeeState がある）で、
   * 受け口の最後の応答が今のカートに合うときだけ見る（合わない間は計算中で止まっている。応答が無ければ止めない）
   */
  function minimumBlock() {
    var last = state.last;
    var sig = last ? domSignature() : null;
    if (sig === null || sig !== last.sig) return '';
    if (state.blockFor !== last) {
      var items = last.response && Array.isArray(last.response.items) ? last.response.items : [];
      var text = '';
      for (var i = 0; i < items.length && !text; i++) {
        var min = shortOfMinimum(items[i]);
        if (min > 0) text = MESSAGES.minQuantity(min);
      }
      state.blockFor = last;
      state.blockText = text;
    }
    return state.blockText;
  }

  function noteFor(button) {
    var anchor = (button.closest && button.closest('.cart__ctas')) || button;
    var next = anchor.nextElementSibling;
    if (next && next.classList && next.classList.contains('pz-cart-fees-note')) return next;
    var note = document.createElement('p');
    note.className = 'pz-cart-fees-note';
    note.setAttribute('role', 'status');
    note.hidden = true;
    anchor.insertAdjacentElement('afterend', note);
    return note;
  }

  /* 今の状態をボタンと知らせに当てる（カートのページの描き直しの後にも呼ぶ） */
  function applyCheckoutState() {
    var busy = isBusy();
    /*
     * デザインの行の値段が待っても入らなかったとき・最少個数より少ないまとまりがある間も、計算中と同じ止め方で止める
     * （理由の文をボタンの横に出す）
     */
    var blocked = (state.priceWait && state.priceWait.failed ? MESSAGES.priceUnavailable : '') || minimumBlock();
    document.documentElement.classList.toggle(CONFIG.busyClass, busy);
    document.documentElement.classList.toggle(CONFIG.blockedClass, !!blocked);
    var buttons = document.querySelectorAll(CONFIG.checkoutSelector);
    if (buttons.length === 0) return;
    injectStyle();
    Array.prototype.forEach.call(buttons, function (b, i) {
      if (busy || blocked) {
        if (!b.hasAttribute('data-pz-fees-lock')) {
          b.setAttribute('data-pz-fees-lock', b.disabled ? 'keep' : 'set');
          b.disabled = true;
        }
      } else if (b.hasAttribute('data-pz-fees-lock')) {
        if (b.getAttribute('data-pz-fees-lock') === 'set') b.disabled = false;
        b.removeAttribute('data-pz-fees-lock');
      }
      if (i > 0) return;
      /* 変わるときだけ書き換える（カートのページはページ全体の書き換えを見張って呼び直すので、同じ値を書くと止まらない） */
      var note = noteFor(b);
      var text = busy ? '料金を計算しています…' : blocked || state.message || '';
      if (note.textContent !== text) note.textContent = text;
      if (note.hidden !== !text) note.hidden = !text;
      note.classList.toggle('pz-cart-fees-note--error', !busy && !!text);
    });
  }

  function setBusyTimer() {
    if (state.busyTimer) window.clearTimeout(state.busyTimer);
    state.busyTimer = window.setTimeout(function () {
      /* 計算の依頼が来ないまま止まった（スクリプトの失敗など）: ボタンを戻す */
      if (state.dirty && state.active === 0) {
        warn('計算が始まらないのでボタンを戻します');
        state.dirty = false;
        applyCheckoutState();
      }
    }, CONFIG.busyTimeoutMs);
  }

  /* カートが変わった（これから数え直す）: 計算が終わるまでボタンを止める */
  function markDirty() {
    state.dirty = true;
    state.dirtySeq++;
    setBusyTimer();
    applyCheckoutState();
  }

  /* ------------------------------------------------------------------ */
  /* 受け口の最後の応答（カートの表示の値）                               */
  /* ------------------------------------------------------------------ */

  /* 応答を覚えて知らせる（sig = その応答に合わせた後のカート）。sessionStorage には失敗しても気にしない */
  function remember(sig, response) {
    if (!response || response.ok !== true) return;
    state.last = { sig: sig, response: response, at: Date.now() };
    try {
      window.sessionStorage.setItem(CONFIG.storageKey, JSON.stringify(state.last));
    } catch (e) {
      /* 使えない環境（プライベートブラウズ等）は覚えるだけ */
    }
    try {
      document.dispatchEvent(new CustomEvent('pz:cart-fees', { detail: { sig: sig, response: response } }));
    } catch (e) {
      /* 古いブラウザ */
    }
  }

  /* カート（signature）に合う最後の応答（無ければ null）。専用ページで覚えたものも使う */
  function responseFor(sig) {
    if (state.last && state.last.sig === sig) return state.last.response;
    try {
      var saved = JSON.parse(window.sessionStorage.getItem(CONFIG.storageKey) || 'null');
      if (saved && saved.sig === sig && saved.response && Date.now() - Number(saved.at || 0) < CONFIG.storageMaxAgeMs) {
        state.last = saved;
        return saved.response;
      }
    } catch (e) {
      /* 読めなければ無し */
    }
    return null;
  }

  /* #PzCartFeeState（カートのページを描くたびに Liquid が書く）のカートの signature。無ければ null */
  function domSignature() {
    var el = document.getElementById('PzCartFeeState');
    if (!el) return null;
    return (el.getAttribute('data-pz-fee-lines') || '')
      .split('|')
      .filter(function (s) { return s !== ''; })
      .sort()
      .join('|');
  }

  /* ------------------------------------------------------------------ */
  /* そろえる                                                            */
  /* ------------------------------------------------------------------ */

  /*
   * エディタのカートとの同期（assets/pz-editor-cart.js。カートのページを開いたときに 1 回、お店のカートを書き換える）の途中か。
   * 同期の順番待ちが空なら settled() はすぐ終わるので、次の区切り（setTimeout 0）より先に終わるかで見分ける
   */
  function editorCartBusy() {
    var ec = window.PzEditorCart;
    if (!ec || typeof ec.settled !== 'function') return Promise.resolve(false);
    var idle = function () { return false; };
    return Promise.race([ec.settled().then(idle, idle), wait(0).then(function () { return true; })]);
  }

  /*
   * portal に問い合わせて、返ってきた料金の行をカートに当てる（body は lines・cartToken ほか）。戻り値は sync の結果の形。
   * mayDefer: アクリルキーホルダーの区分の付け替えがある応答は、エディタのカートとの同期の途中なら当てずに deferred で返す
   * （同期は始めたときのカートを見て同じ付け替えの行を足すので、両方が足すと個数が二重になる。同期が終わってから数え直す）
   */
  function askAndApply(items, cart, extra, mayDefer) {
    var sig = signature(items);
    var body = { lines: payloadLines(items), cartToken: cart && cart.token ? String(cart.token) : null };
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) body[k] = extra[k];
    state.calling = true;
    applyCheckoutState();
    return askPortal(body)
      .then(function (r) {
        var j = r.json;
        if (!j || j.ok !== true) {
          /* 404 = portal にまだ受け口が無い・機能が止まっている（今のテーマと同じ動きのまま）。知らせない */
          var available = !(r.status === 404 || (j && j.code === 'not_found'));
          warn('料金の計算に失敗', r.status, j && j.code);
          return { ok: false, changed: false, status: r.status, code: (j && j.code) || null, available: available };
        }
        var apply = function () {
          return applyNow(j);
        };
        if (!mayDefer || !Array.isArray(j.retier) || j.retier.length === 0) return apply();
        return editorCartBusy().then(function (busy) {
          return busy ? { ok: true, changed: false, deferred: true } : apply();
        });
      })
      .then(
        function (r) {
          state.calling = false;
          return r;
        },
        function (e) {
          state.calling = false;
          throw e;
        }
      );

    function applyNow(j) {
      return applyChanges(j, items).then(function (res) {
        /* 足せない・消せない行があった: 知らせる（次の問い合わせでやり直す。noteApplied） */
        var result = res.failed
          ? { ok: false, changed: res.changed, code: 'fee_lines_failed', available: true, response: j }
          : { ok: true, changed: res.changed, response: j };
        if (!res.changed) {
          noteApplied(sig, res.failed);
          remember(sig, j);
          return result;
        }
        log('料金の行を作り直しました', 'add', (j.add || []).length, 'remove', (j.remove || []).length);
        /* 足した・消した後のカートを覚える（次に同じカートなら問い合わせない） */
        return getCart().then(function (after) {
          var afterSig = signature((after && after.items) || []);
          noteApplied(afterSig, res.failed);
          remember(afterSig, j);
          return result;
        });
      });
    }
  }

  function runOnce(force) {
    return getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var sig = signature(items);
      if (!force && sig === state.synced) return { ok: true, changed: false, skipped: true };
      /* 料金の行を続けて当てられなかったカート: 同じカートのままなら問い合わせない（知らせは出したまま） */
      if (!force && sig === state.failedSig) return { ok: false, changed: false, skipped: true, code: 'fee_lines_failed', available: true };
      if (!needsFees(items)) {
        state.synced = sig;
        return { ok: true, changed: false, skipped: true };
      }
      return askAndApply(items, cart, null, !force);
    });
  }

  /*
   * 処理を順番待ちに入れる（前の処理が終わってから始める）。終わったら、始めた後にカートが変わっていなければ
   * （markDirty されていなければ）止めていたボタンを戻す（変わっていれば次の計算で戻す）
   */
  function enqueue(task) {
    state.active++;
    var p = state.queue.then(function () {
      var seq = state.dirtySeq;
      return Promise.resolve()
        .then(task)
        .then(
          function (r) {
            return { r: r, seq: seq };
          },
          function (e) {
            warn('料金の計算に失敗', e && e.message);
            return { r: { ok: false, changed: false, code: 'network', available: true }, seq: seq };
          }
        );
    });
    state.queue = p.then(null, function () {});
    return p.then(function (x) {
      state.active--;
      if (state.active === 0 && state.dirtySeq === x.seq) state.dirty = false;
      applyCheckoutState();
      return x.r;
    });
  }

  /* 結果の知らせ（料金の計算に失敗したとき） */
  function noteResult(r) {
    state.message = r.ok || r.available === false ? null : '製版代・洗浄料を計算できませんでした。ページを再読み込みしてください。';
  }

  /* カートを変えたときの描き直し（失敗しても続ける） */
  function callChanged(fns) {
    return Promise.all(
      fns.map(function (fn) {
        return Promise.resolve()
          .then(fn)
          .then(null, function () {});
      })
    );
  }

  /*
   * カートの料金の行をそろえる。opts.force = true なら前と同じカートでも問い合わせる。
   * opts.onChanged: カートを変えたときに呼ぶ（カートのページの描き直し。Promise を返せば待つ）。
   * まだ始まっていない問い合わせがあれば 1 回にまとめる（始まった後の依頼はその次に問い合わせる）。
   * 戻り値は Promise（失敗しても reject しない）: { ok, changed, skipped?, response?, status?, code? }
   */
  function sync(opts) {
    opts = opts || {};
    var job = state.pendingSync;
    if (job) {
      job.force = job.force || !!opts.force;
      if (typeof opts.onChanged === 'function') job.onChanged.push(opts.onChanged);
      return job.promise;
    }
    job = { force: !!opts.force, onChanged: typeof opts.onChanged === 'function' ? [opts.onChanged] : [] };
    state.pendingSync = job;
    job.promise = enqueue(function () {
      state.pendingSync = null;
      return runOnce(job.force).then(function (r) {
        noteResult(r);
        if (r.deferred) {
          /* エディタのカートとの同期が終わってから数え直す（それまで「ご購入手続きへ」は止めたまま） */
          markDirty();
          window.PzEditorCart.settled().then(function () {
            sync({ onChanged: function () { return callChanged(job.onChanged); } });
          });
        }
        return (r.changed ? callChanged(job.onChanged) : Promise.resolve()).then(function () {
          return r;
        });
      });
    });
    return job.promise;
  }

  /*
   * 枚数を変える（カートのページのサイズごとの －・＋。段階 2b）。change を付けて portal に問い合わせ、よいと言えば
   * （増やすときは在庫を確かめる）/cart/change.js で枚数を変えてから、返ってきた料金の行を足す・消す。
   * だめなら枚数を変えず、portal の文（例「在庫が足りないため増やせません…」）を message で返す。
   * アクリルキーホルダーの区分の付け替え（応答の retier）でその行ごと新しい区分の行に置き換わるときは、/cart/change.js は呼ばずに
   * 新しい行を足して前の行を消し、新しい行の key を movedTo で返す。
   * opts.onChanged: カートを変えたときに呼ぶ（描き直し）。opts.onMoved(前の key, 新しい key): 行が置き換わったとき、描き直しの前に呼ぶ。
   * 戻り値は Promise（reject しない）: { ok, changed, message?, code?, response?, movedTo? }
   */
  function changeQuantity(key, quantity, opts) {
    opts = opts || {};
    key = String(key || '');
    quantity = Math.max(0, Math.floor(Number(quantity) || 0));
    markDirty();
    return enqueue(function () {
      return getCart().then(function (cart) {
        var items = (cart && cart.items) || [];
        var line = null;
        for (var i = 0; i < items.length; i++) if (String(items[i].key) === key) line = items[i];
        if (!line) return { ok: false, changed: false, code: 'not_in_cart', message: MESSAGES.cartChanged };
        if (Number(line.quantity) === quantity) return { ok: true, changed: false };
        var failedText = isPiecesLine(line) ? MESSAGES.changeFailedPieces : MESSAGES.changeFailed;
        return askPortal({
          lines: payloadLines(items),
          change: { key: key, quantity: quantity },
          cartToken: cart.token ? String(cart.token) : null
        }).then(function (r) {
          var j = r.json;
          if (!j || j.ok !== true) {
            warn('枚数の確認に失敗', r.status, j && j.code);
            return { ok: false, changed: false, code: (j && j.code) || 'portal_error', message: failedText };
          }
          var c = j.change;
          if (!c || c.ok !== true) {
            return { ok: false, changed: false, code: 'refused', message: (c && c.message) || failedText, response: j };
          }
          /*
           * アクリルキーホルダーの区分の付け替えで、変える行ごと新しい区分の行に置き換わる（応答の retier の fromKeys にこの行がある）
           * ときは、この行の枚数は変えずに、新しい行を足してから前の行を消す（足せなかったときにカートが変わらないように）
           */
          var retier = retierOf(j, key);
          var changeLine = retier ? Promise.resolve({ ok: true }) : postJson(CONFIG.cartChange, { id: key, quantity: quantity });
          return changeLine.then(function (cr) {
            if (!cr.ok) {
              warn('枚数を変えられません', cr.status);
              return { ok: false, changed: false, code: 'cart_change_failed', message: failedText };
            }
            return applyChanges(j, items)
              .then(function (res) {
                return getCart().then(function (after) {
                  return { res: res, after: after };
                });
              })
              .then(function (x) {
                var afterItems = (x.after && x.after.items) || [];
                /* 付け替えの行を足せなかった: カートは変わっていない（この行の枚数も変えていない） */
                if (retier && !x.res.changed) {
                  return { ok: false, changed: false, code: 'cart_change_failed', message: failedText };
                }
                var afterSig = signature(afterItems);
                noteApplied(afterSig, x.res.failed);
                remember(afterSig, j);
                var out = { ok: true, changed: true, response: j };
                var movedTo = retier ? retierLineKey(retier, afterItems) : null;
                if (movedTo) out.movedTo = movedTo;
                /* 枚数は変わったが料金の行を足せない・消せない行があった（429 など）: 続けて問い合わせてそろえ直す */
                if (x.res.failed) {
                  out.resync = true;
                  return out;
                }
                state.message = null;
                return out;
              }, function (e) {
                /* 枚数は変わったが料金の行をそろえられなかった: 次の問い合わせでそろえ直す */
                warn('料金の行をそろえられません', e && e.message);
                state.synced = null;
                return { ok: true, changed: true, response: j, resync: true };
              });
          });
        });
      }).then(function (res) {
        if (res.movedTo && typeof opts.onMoved === 'function') {
          try {
            opts.onMoved(key, res.movedTo);
          } catch (e) {
            /* 引き継げなくても続ける */
          }
        }
        return (res.changed && typeof opts.onChanged === 'function' ? callChanged([opts.onChanged]) : Promise.resolve()).then(function () {
          if (res.resync) sync({ force: true, onChanged: opts.onChanged });
          return res;
        });
      });
    });
  }

  /*
   * 行を消す（カートのページの商品のまとまりの「削除」）。/cart/update.js で消してから、料金の行をそろえる。
   * 戻り値は Promise（reject しない）: { ok, changed, message? }
   */
  function removeLines(keys, opts) {
    opts = opts || {};
    var updates = {};
    (keys || []).forEach(function (k) { updates[String(k)] = 0; });
    if (Object.keys(updates).length === 0) return Promise.resolve({ ok: true, changed: false });
    markDirty();
    return enqueue(function () {
      return postJson(CONFIG.cartUpdate, { updates: updates }).then(function (r) {
        if (!r.ok) {
          warn('行を消せません', r.status);
          return { ok: false, changed: false, code: 'cart_update_failed', message: MESSAGES.removeFailed };
        }
        return runOnce(true).then(function (s) {
          noteResult(s);
          return { ok: true, changed: true, fees: s };
        });
      }).then(function (res) {
        return (res.changed && typeof opts.onChanged === 'function' ? callChanged([opts.onChanged]) : Promise.resolve()).then(function () {
          return res;
        });
      });
    });
  }

  /*
   * カートのページ: デザイン・料金の行（隠し項目 _pz_ のある行）の key と数量が、前に見たときと変わっていたら markDirty
   * （計算が終わるまでボタンを止める）して true。snippets/pz-cart-options.liquid の #PzCartFeeState（ページを描くたびに
   * Liquid が書く）を読む。デザインの行も料金の行も無いカートでは止めない
   */
  var lastDom = null;
  function noteCartDom() {
    var el = document.getElementById('PzCartFeeState');
    if (!el) return false;
    var sig = el.getAttribute('data-pz-fee-lines') || '';
    var changed = sig !== lastDom;
    lastDom = sig;
    if (!changed || el.getAttribute('data-pz-needs-fees') !== 'true') {
      applyCheckoutState();
      return false;
    }
    markDirty();
    return true;
  }

  window.PzCartFees = {
    config: CONFIG,
    sync: sync,
    changeQuantity: changeQuantity,
    removeLines: removeLines,
    markDirty: markDirty,
    noteCartDom: noteCartDom,
    applyCheckoutState: applyCheckoutState,
    isBusy: isBusy,
    /* アクリルキーホルダーの最少個数: まとまりが最少個数より少ないか（その最少個数か 0）・知らせの文 */
    shortOfMinimum: shortOfMinimum,
    minQuantityMessage: MESSAGES.minQuantity,
    /* 受け口の最後の応答（カートの表示の値）: domSignature() のカートに合うもの・最後のもの */
    responseFor: responseFor,
    domSignature: domSignature,
    lastResponse: function () {
      return state.last ? state.last.response : null;
    },
    /* 最後に読んだカートの数（枚数を変えた・行を消した後に、カートが空になったかを見る） */
    itemCount: function () {
      return state.itemCount;
    }
  };
})();
